import { createStore } from "solid-js/store";
import { Show, onMount } from "solid-js";
import _ from "underscore";
import { A, useNavigate, useRouteData } from "@solidjs/router";
import { fetchPost, scrollToTop } from "../library/common";
import { FacultyMemberModel, ListItem } from "../types";
import { useForm } from "../components/universal/validation";
import { Progress } from "../components/progress";
import { Required } from "../components/universal/required-icon";
import { getArtworksCountJson } from "../app";
import { CMS } from "../components/cms/cms";
import { ModelEditor, ModelImageCropper, ModelProvider, ModelSelect, ModelTextBox } from "../components/model-form-elements";
import { Img } from "../components/universal/image";
import toast from "solid-toast";
import { PageLoading } from "../components/page-loading";

type State = {
    isContentReady: boolean;
    showImageCropper: boolean;
}

type ButtonActiveState = {
    submit: boolean;
}

type Resources = {
    Artists: ListItem[];
}

export function EditFacultyMember() {
    const [resources, setResources] = createStore<Resources>({ Artists: [] });
    const [state, setState] = createStore<State>({ isContentReady: false, showImageCropper: false } as State);
    const [buttonActive, setButtonActive] = createStore<ButtonActiveState>({ submit: true } as ButtonActiveState);
    const { formSubmit, validate, errors, setErrors } = useForm({
        errorClass: "error-input"
    });  
    const navigate = useNavigate();
    const [model, setModel] = createStore<FacultyMemberModel>({
        FirstName: "",
        LastName: "",
        Position: ""
    } as FacultyMemberModel);
    const getModel = async () => {
        const imageFile = await model.PhotoEditor?.getImageFile();
        const filename = imageFile?.filename || "";
        const extension = filename.split('.').at(-1);
        return {
            ...model,
            Biography: model.BiographyEditor?.getHtml(),
            NewImageId: model.Photo?.Id,
            ImageFile: imageFile?.image,
            OriginalFilename: filename,
            FilenameExtension: extension
        };
    };
    const validateOtherFields = async () => {
        var isValid = true;
        const imageFile = await model.PhotoEditor?.getImageFile();
        if (!imageFile) {
            if (!model.Photo?.URL) {
                toast("Please ensure that you have attached a photo.");
                isValid = false;    
            }
        }
        else {
            if (!imageFile?.isValid) {
                toast("Please ensure that you have uploaded a photo in the correct image file type (JPG, PNG).");
                isValid = false;
            }
        }
        return isValid;
    };
    const submitIfValid = async () => {
        setButtonActive("submit", false);
        const isValid = await validateOtherFields();
        if (!isValid) {
            return;
        }
        await save();
        await getArtworksCountJson({ forceRefresh: true });
        setButtonActive("submit", true);
    };
    const save = async () => {
        const model = await getModel();
        const res = await fetchPost("/AtelierApplication/SaveFacultyMemberJson", model);
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        navigate("/faculty");
    };
    const changeImageClicked = (e: Event) => {
        e.preventDefault();
        setState("showImageCropper", true);
    }
    onMount(async () => {
        const routeData = await useRouteData() as any;
        let facultyMember = routeData.facultyMember as FacultyMemberModel;
        setModel(facultyMember as FacultyMemberModel);
        setResources("Artists", routeData.resources.Artists);
        if (facultyMember.Photo) {
            setModel("Photo", facultyMember.Photo);
        }
        setState({ isContentReady: true, showImageCropper: !facultyMember.Photo?.URL });
        scrollToTop();
    });
    return (
        <>
            <Show when={state.isContentReady} fallback={<PageLoading />}>
                <ModelProvider model={model} setModel={setModel} validation={{ errors, validate }}>
                    <div>
                        <Progress step={2} />
                        <form use:formSubmit={submitIfValid}>
                            <div class="row">
                                <div class="col-md-6" style="padding-right: 20px;">
                                    <div class="form-group">
                                        <h4 class="m-t-xxl m-b-lg" style="font-family: 'Oswald'; font-size: 3em;">
                                            <CMS id="label::artwork-details">Faculty member</CMS>
                                            <Show when={!!model.Id}>
                                                <small># {model.Id}</small>
                                            </Show>
                                        </h4>
                                        <p>
                                            <CMS id="message::required-fields">
                                                Fields that are marked with a <span class="fa fa-star" style="color: red;"></span> are required.
                                            </CMS>
                                        </p>
                                        <div class="row">
                                            <div class="col-md-4">
                                                <ModelTextBox<FacultyMemberModel>
                                                    field="FirstName" label="First name"
                                                    required
                                                />
                                            </div>
                                            <div class="col-md-4">
                                                <ModelTextBox<FacultyMemberModel>
                                                    field="LastName" label="Last name"
                                                    required
                                                />
                                            </div>
                                            <div class="col-md-4">
                                                <ModelTextBox<FacultyMemberModel>
                                                    field="Position" label="Position at the school"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div class="row" style="margin-top: 20px;">
                                            <div class="col-md-12">
                                                <ModelSelect<FacultyMemberModel>
                                                    field="ArtistId"
                                                    list={resources.Artists}
                                                    label={() => <>ARC Living Artist <small>Select if faculty member is an ARC Living Artist</small></>}
                                                />
                                            </div>
                                        </div>
                                        <div class="row" style="margin-top: 20px;">
                                            <div class="col-md-12">
                                                <ModelEditor<FacultyMemberModel>
                                                    field="Biography" editor="BiographyEditor"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6" style="padding-left: 20px;">
                                    <CMS id="scholarship-application/edit-artwork::images/instructions">
                                        <h4>
                                            Image
                                        </h4>
                                        <p>
                                            Image should be in either JPEG or PNG format and should be between 1MB and 10MB in size.
                                        </p>
                                    </CMS>
                                    <div style="border: 1px solid #ccc; padding: 30px; margin: 0px; margin-top: 25px; border-radius: 10px;">
                                        <div>
                                            <label style="font-size: 1.5em;">
                                                <Required /> <CMS id="label::image" text>Image</CMS>
                                            </label>
                                            <Show when={state.showImageCropper}>
                                                <ModelImageCropper<FacultyMemberModel>
                                                    imageUrl={() => model.Photo?.URL}
                                                    editor="PhotoEditor"
                                                    required
                                                />    
                                            </Show>
                                            <Show when={!state.showImageCropper}>
                                                <div>
                                                    <div>
                                                        <Img src={model.Photo.URL} width={800} style="width: 100%;" />
                                                    </div>
                                                    <div style="display: table-cell; padding: 10px; vertical-align: middle; width: 100%;">
                                                        <button class="btn btn-primary" onClick={changeImageClicked}>Change Image</button>
                                                    </div>
                                                </div>
                                            </Show>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Show when={model.Id == undefined}>
                                <div class="row">
                                    <div class="col-md-6">
                                        <p>
                                            <CMS id="salon-application/edit-artwork::message/please-be-patient">
                                                <strong>Note:</strong> After you press the button to submit it may take quite a while for your image to upload, please be patient!
                                            </CMS>
                                        </p>
                                    </div>
                                </div>
                            </Show>
                            <div class="row m-t-xxl">
                                <div class="col-md-12">
                                    <div id="submit-row">
                                        <A type="button" class="btn btn-default" href={"/faculty"}><CMS id="button::cancel">Cancel</CMS></A>
                                        <button type="submit" class="btn btn-primary" classList={{ ["disable"] : !buttonActive.submit }} style="margin-right: 20px;">
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </ModelProvider>
            </Show>
        </>
    );
}